import { useEffect, useState, useCallback } from 'react';
import { apiHeaders } from '../lib/consoleApi';
import { readSession } from '../auth/cognito';

const DATA_URL = import.meta.env.VITE_DATA_URL || '';
const OVERDUE_MS = 15 * 60 * 1000; // due-now older than this = overdue (red badge)
const DONE_WINDOW_MS = 24 * 60 * 60 * 1000; // Done group shows the last 24h only

// Shared scheduled-callbacks source for the Task tab, the rail badge, and the
// waiting-screen strip. Each consumer polls independently (30s, tiny GET) —
// acceptable at current scale; lift into context if consumers multiply.
export function useCallbacks(enabled) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!DATA_URL) { setErr('Data endpoint not configured (VITE_DATA_URL).'); return; }
    // 2026-09-17: the agent's own entitled tenants (same source
    // OutboundDialer already uses for its tenant list) — NOT a build-time
    // constant. Was previously hardcoded to VITE_TENANT_ID, which deploy.sh
    // never overrides, so any tenant other than whatever that constant
    // happened to be was silently invisible in this list on every deploy.
    // See docs/ops/callback-ownership-tasklist-investigation-2026-09-17.md.
    const tenants = readSession()?.tenants || [];
    if (!tenants.length) { setRows([]); setErr(null); return; }
    const headers = await apiHeaders();
    const settled = await Promise.allSettled(tenants.map(async t => {
      const res = await fetch(`${DATA_URL}?type=callbacks&tenantId=${encodeURIComponent(t)}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    }));
    // A failure for one tenant should not blank the list for every other
    // tenant an agent is entitled to — merge what succeeded, report the rest.
    const ok = settled.filter(s => s.status === 'fulfilled').flatMap(s => s.value);
    const failedCount = settled.filter(s => s.status === 'rejected').length;
    setRows(ok);
    setErr(
      failedCount === 0 ? null
      : failedCount === tenants.length ? (settled.find(s => s.status === 'rejected')?.reason?.message || 'Load failed')
      : `${failedCount} of ${tenants.length} tenants' callbacks failed to load`
    );
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [enabled, load]);

  const now = Date.now();
  const all = rows || [];
  const open = all.filter(r => r.status === 'task-scheduled');
  const due = open.filter(r => new Date(r.dispatchAt).getTime() <= now);
  const upcoming = open
    .filter(r => new Date(r.dispatchAt).getTime() > now)
    .sort((a, b) => new Date(a.dispatchAt) - new Date(b.dispatchAt));
  const done = all
    .filter(r => r.status !== 'task-scheduled'
      && now - new Date(r.dispatchAt).getTime() < DONE_WINDOW_MS)
    .sort((a, b) => new Date(b.dispatchAt) - new Date(a.dispatchAt));
  const overdue = due.some(r => now - new Date(r.dispatchAt).getTime() > OVERDUE_MS);

  return {
    rows, err, load, due, upcoming, done,
    dueCount: due.length,
    overdue,
    nextUpcoming: upcoming[0] || null,
    loaded: rows !== null,
  };
}

export function fmtSyd(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    weekday: 'short', hour: 'numeric', minute: '2-digit',
    hour12: true, timeZone: 'Australia/Sydney',
  });
}
